import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "~/utils/trpc";
import { useState, useEffect } from "react";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Input } from "@quake/shared-ui/components/ui/input";
import { ColorPicker } from "@quake/shared-ui/components/ui/color-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@quake/shared-ui/components/ui/select";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import { toast } from "@quake/shared-ui/lib/sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@quake/shared-ui/components/ui/table";
import type { AttrType } from "@quake/isomorphic/dataFilters";
import { makeAttributeId } from "@quake/isomorphic";
import { DEFAULT_CLIENT_STATUSES, type ClientStatus, type StatusType } from "@quake/isomorphic";
import { StatusTypeSelect } from "~/components/base/StatusTypeSelect";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { AttributesDialog } from "~/components/AttributesDialog";
import { Separator } from "@quake/shared-ui/components/ui/separator";
import { DeleteConfirmationDialog } from "~/components/DeleteConfirmationDialog";
import { Label } from "@quake/shared-ui/components/ui/label";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/clients/settings")({
  component: RouteComponent,
});

let statusIdCounter = 0;
const createStatusId = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${statusIdCounter++}`;

function RouteComponent() {
  const { data: session, isLoading: isSessionLoading } = useQuery(trpc.auth.getSession.queryOptions());
  const { data: settings } = useQuery(trpc.clientsSettings.get.queryOptions());
  const updateSettings = useMutation(trpc.clientsSettings.update.mutationOptions());
  const [attributes, setAttributes] = useState<AttrType[]>([]);
  const [statuses, setStatuses] = useState<ClientStatus[]>([]);
  const [defaultStatusId, setDefaultStatusId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState({
    name: "",
    description: "",
    color: "#2196F3",
    type: "active" as StatusType,
  });
  const qc = useQueryClient();

  const attr: AttrType = {
    id: "",
    name: "",
    description: "",
    searchable: false,
    type: "Text",
    table_reference: "",
    options: [] as { value: string; label: string; index: number }[],
    defaultValue: "",
    boundaries: { required: false, unique: false },
  };

  const entityTypes = [
    "Text",
    "Number",
    "Checkbox",
    "Date",
    "TimeStamp",
    "Select",
    "Multi Select",
    "Record",
    "Multi Record",
  ] as const;
  const optionsList = ["Select", "Multi Select"] as const;

  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [selectedAttribute, setSelectedAttribute] = useState(attr);
  const [attrIndex, setAttrIndex] = useState(-1);
  const [attributeToDeleteIndex, setAttributeToDeleteIndex] = useState<number | null>(null);
  const { datasetSlug } = useParams({ from: "/_dashboard/$datasetSlug" });
  const canCreateAttributes = session?.user?.role === "Administrator";

  useEffect(() => {
    setAttributes(settings?.attributes || []);
    const nextStatuses = settings?.statuses ?? DEFAULT_CLIENT_STATUSES;
    setStatuses(nextStatuses);
    setDefaultStatusId(settings?.default_status_id || nextStatuses[0]?.id || null);
  }, [settings]);

  useBreadCrumbs([
    { title: "Clients", link: `/${datasetSlug}/data/clients` },
    { title: "Settings", link: `/${datasetSlug}/data/clients/settings` },
  ]);

  const save = async () => {
    const normalizedStatuses = statuses.map((status, index) => ({ ...status, order: index }));
    try {
      await updateSettings.mutateAsync({
        attributes,
        statuses: normalizedStatuses,
        default_status_id: defaultStatusId,
      });
      await Promise.all([
        qc.invalidateQueries(trpc.clientsSettings.get.queryFilter()),
        qc.invalidateQueries({ queryKey: trpc.data.getRecruitmentClients.queryKey() }),
        qc.invalidateQueries({ queryKey: trpc.entityViews.getViews.queryKey({ entityType: "recruitment_clients" }) }),
        qc.invalidateQueries({ queryKey: trpc.entityViews.getViewBySlug.queryKey() }),
        qc.invalidateQueries({
          queryKey: trpc.entityViews.getAvailableFields.queryKey({ entityType: "recruitment_clients" }),
        }),
      ]);
      toast.success("Client settings saved successfully");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save settings");
    }
  };

  const handleAddClick = () => {
    setDialogMode("add");
    setSelectedAttribute(attr);
    setAttributeDialogOpen(true);
  };

  const handleEditClick = (a: typeof attr, idx: number) => {
    setDialogMode("edit");
    setSelectedAttribute(a);
    setAttrIndex(idx);
    setAttributeDialogOpen(true);
  };

  const handleDialogSave = (newAttr: typeof attr) => {
    let list = [...attributes];
    if (dialogMode === "edit" && attrIndex !== -1) {
      list = list.filter((_, idx) => idx !== attrIndex);
    }
    list = [...list, { ...newAttr }];
    setAttributes(list);
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setSelectedAttribute(attr);
    setAttrIndex(-1);
    setAttributeDialogOpen(false);
  };

  const removeAttribute = (index: number) => {
    setAttributeToDeleteIndex(index);
  };

  const confirmDeleteAttribute = () => {
    if (attributeToDeleteIndex !== null) {
      setAttributes((prev) => prev.filter((_, i) => i !== attributeToDeleteIndex));
      setAttributeToDeleteIndex(null);
    }
  };

  const updateStatus = (index: number, data: Partial<ClientStatus>) => {
    setStatuses((prev) => {
      const list = [...prev];
      list[index] = { ...list[index], ...data } as ClientStatus;
      return list;
    });
  };

  const removeStatus = (index: number) => {
    setStatuses((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (prev[index]?.id === defaultStatusId) setDefaultStatusId(next[0]?.id || null);
      return next;
    });
  };

  const addStatus = () => {
    const trimmedName = newStatus.name.trim();
    if (!trimmedName) return;
    if (statuses.some((s) => s.name.trim().toLowerCase() === trimmedName.toLowerCase())) {
      toast.error("A status with this name already exists.");
      return;
    }
    const id = createStatusId();
    setStatuses((prev) => [
      ...prev,
      {
        id,
        name: trimmedName,
        description: newStatus.description,
        color: newStatus.color,
        type: newStatus.type,
        order: prev.length,
      },
    ]);
    if (!defaultStatusId) setDefaultStatusId(id);
    setNewStatus({ name: "", description: "", color: "#2196F3", type: "active" as StatusType });
  };

  const isLoading = updateSettings.isPending;
  const hasError = updateSettings.isError;
  const errorMessage =
    updateSettings.error instanceof Error
      ? updateSettings.error.message
      : "Failed to save client settings. Please try again.";

  return (
    <div className="space-y-4 mx-5 pb-8">
      <h1 className="text-2xl font-bold">Clients Settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>Statuses</CardTitle>
          <CardDescription>
            Configure the client statuses available when creating and updating client records.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mt-4 items-center">
            <ColorPicker
              selectedColor={newStatus.color}
              onColorChange={(color) => setNewStatus((prev) => ({ ...prev, color }))}
            />
            <Input
              value={newStatus.name}
              onChange={(e) => setNewStatus((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="New status"
              className="w-32"
            />
            <Input
              value={newStatus.description}
              onChange={(e) => setNewStatus((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Description"
            />
            <StatusTypeSelect value={newStatus.type} onChange={(type) => setNewStatus((prev) => ({ ...prev, type }))} />
            <Button type="button" onClick={addStatus}>
              Add
            </Button>
          </div>
          <ul className="space-y-2 mt-4">
            {statuses.map((status, index) => (
              <li key={status.id} className="flex items-center gap-2">
                <ColorPicker selectedColor={status.color} onColorChange={(color) => updateStatus(index, { color })} />
                <Input
                  value={status.name}
                  onChange={(e) => updateStatus(index, { name: e.target.value })}
                  className="w-32"
                />
                <Input
                  value={status.description || ""}
                  onChange={(e) => updateStatus(index, { description: e.target.value })}
                />
                <StatusTypeSelect value={status.type} onChange={(type) => updateStatus(index, { type })} />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove status"
                  onClick={() => removeStatus(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Label>Default Client Status</Label>
            <Select value={defaultStatusId || undefined} onValueChange={(value) => setDefaultStatusId(value || null)}>
              <SelectTrigger className="w-60">
                <SelectValue placeholder="Select default client status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Custom Attributes</CardTitle>
          <CardDescription>
            Configure additional fields for clients. These attributes will appear in the client form.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canCreateAttributes ? (
            <div className="text-right">
              <Button type="button" onClick={handleAddClick}>
                <PlusIcon className="mr-1 h-4 w-4" /> Add Attribute
              </Button>
            </div>
          ) : isSessionLoading ? (
            <Alert>
              <AlertDescription>Loading session information...</AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertDescription>Only administrators can create client custom attributes.</AlertDescription>
            </Alert>
          )}
          <AttributesDialog
            open={attributeDialogOpen}
            onOpenChange={setAttributeDialogOpen}
            attribute={selectedAttribute}
            onSave={handleDialogSave}
            onCancel={handleDialogCancel}
            mode={dialogMode}
            optionsList={optionsList}
            entityTypes={entityTypes}
            existingNames={attributes
              .filter((a, idx) => dialogMode === "add" || idx !== attrIndex)
              .map((a) => a.name.trim())}
            existingIds={attributes
              .filter((a, idx) => dialogMode === "add" || idx !== attrIndex)
              .map((a) => a.id?.trim() || makeAttributeId(a.name))}
          />
          <Table useDynamicHeight={false}>
            <TableHeader>
              <TableRow className="bg-muted-foreground/5">
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Unique</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributes.map((a, idx) => (
                <TableRow key={idx}>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>
                    <code>{a.id?.trim() || makeAttributeId(a.name)}</code>
                  </TableCell>
                  <TableCell>{a.type}</TableCell>
                  <TableCell>{a.boundaries?.required ? "Yes" : "No"}</TableCell>
                  <TableCell>{a.boundaries?.unique ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 cursor-pointer" onClick={() => removeAttribute(idx)} />
                      <EditIcon
                        className="h-4 w-4 cursor-pointer text-green-500"
                        onClick={() => handleEditClick(a, idx)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {hasError && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <Button onClick={save} disabled={isLoading}>
        {isLoading ? "Saving..." : "Save"}
      </Button>

      <Separator className="my-6" />

      <DeleteConfirmationDialog
        open={attributeToDeleteIndex !== null}
        onOpenChange={(open) => !open && setAttributeToDeleteIndex(null)}
        onConfirm={confirmDeleteAttribute}
        description={`This will permanently delete the attribute "${
          attributeToDeleteIndex !== null ? attributes[attributeToDeleteIndex]?.name : ""
        }". This action cannot be undone.`}
      />
    </div>
  );
}
